import { Button, message, Popconfirm } from 'antd';
import { useTranslate } from '@tolgee/react';
import styled from '@emotion/styled';
import { ButtonProps } from 'antd/es/button';
import { confirmReturn, tradeAcceptOfferV1 } from 'services/api/return';
import { useSystemLanguageValue } from 'hooks/business/system/useSystem';
import { isTiKTokUpperCase } from 'lib/utils/isTikTok';
import { getApiErrorCodeMsg } from 'services/api/utils/errorCode';
import { useRef, useState } from 'react';

const ButtonDisabled = styled(Button)`
  color: #b5b9bf !important;
`;

export interface ConfirmReturnRefundProps extends ButtonProps {
  externalStoreId: string;
  externalTradeId: string;
  returnSn: string;
  platform?: General.PlatformUpperCase;
  returnOrderItemId?: string[];
  onSucceed?: () => void;
}

/** 接受退货退款申请; */
const ConfirmReturnRefund = (props: ConfirmReturnRefundProps) => {
  const {
    externalStoreId,
    externalTradeId,
    returnSn,
    platform,
    returnOrderItemId,
    onSucceed,
    disabled,
    onClick: onClickProp,
    ...otherProps
  } = props;

  const { t } = useTranslate();
  const sysLang = useSystemLanguageValue();
  const [visible, setVisible] = useState(false);
  const preventDefault = useRef(false);

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    onClickProp?.(e);
    if (e.defaultPrevented) {
      preventDefault.current = true;
      return;
    }
    setVisible(true);
  };

  const handleVisibleChange = (visible: boolean) => {
    if (preventDefault.current) {
      preventDefault.current = false;
      return;
    }
    setVisible(visible);
  };

  const handleReject = (err: any) => {
    if (err?.response?.data?.code === 1029) {
      try {
        const json = JSON.parse(err.response.data.msg);
        const msg = json[sysLang];
        message.error(msg);
      } catch (error) {
        message.error(t('translation.操作失败，请稍后再试'));
      }
    } else {
      message.error(getApiErrorCodeMsg(err.response));
    }
  };

  if (platform === 'SHOPEE' && returnSn) {
    return (
      <Popconfirm
        visible={visible}
        onVisibleChange={handleVisibleChange}
        disabled={disabled}
        title={t('ViewRefund.确认同意买家的退货退款申请吗？')}
        onConfirm={() => {
          tradeAcceptOfferV1({
            returnExternalId: returnSn,
            operate: 'CONFIRM_RETURN_REFUND',
          })
            .then(() => {
              if (onSucceed) {
                onSucceed();
              }
            })
            .catch((err) => {
              handleReject(err);
            });
        }}
      >
        <Button size="small" disabled={disabled} {...otherProps} onClick={handleClick}>
          {t('trade.接受退货退款申请')}
        </Button>
      </Popconfirm>
    );
  }

  if (platform === 'LAZADA' && returnOrderItemId) {
    return (
      <Popconfirm
        visible={visible}
        onVisibleChange={handleVisibleChange}
        disabled={disabled}
        title={t('ViewRefund.确认同意买家的退货退款申请吗？')}
        onConfirm={() => {
          confirmReturn({
            externalStoreId,
            externalTradeId,
            returnSn,
            returnOrderItemId,
            operate: 'CONFIRM_RETURN_REFUND',
            externalType: 'LAZADA',
          })
            .then(() => {
              if (onSucceed) {
                onSucceed();
              }
            })
            .catch((err) => {
              handleReject(err);
            });
        }}
      >
        <Button size="small" disabled={disabled} {...otherProps} onClick={handleClick}>
          {t('ViewRefund.退货退款')}
        </Button>
      </Popconfirm>
    );
  }

  if (isTiKTokUpperCase(platform) && returnOrderItemId) {
    return (
      <Popconfirm
        visible={visible}
        onVisibleChange={handleVisibleChange}
        disabled={disabled}
        title={t('ViewRefund.确认同意买家的退货退款申请吗？')}
        onConfirm={() => {
          confirmReturn({
            externalStoreId,
            externalTradeId,
            returnSn,
            externalType: platform === 'TIKTOK' ? 'tiktok' : platform,
            operate: 'CONFIRM',
          })
            .then(() => {
              if (onSucceed) {
                onSucceed();
              }
            })
            .catch((err) => {
              handleReject(err);
            });
        }}
      >
        <Button size="small" disabled={disabled} {...otherProps} onClick={handleClick}>
          {t('btn_agree_refunds')}
        </Button>
      </Popconfirm>
    );
  }

  return (
    <ButtonDisabled size="small" title={t('trade.暂不支持操作提示')} disabled {...otherProps}>
      {t('trade.接受退货退款申请')}
    </ButtonDisabled>
  );
};

export default ConfirmReturnRefund;
